import React from 'react';
import {
  Box,
  Chip,
  Stack,
  Typography,
  Button,
  Paper,
} from '@mui/material';

type MetaItem = { label: string; value: React.ReactNode };

type MobileEntityCardProps = {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  chips?: React.ReactNode;
  meta?: MetaItem[];
  actions?: React.ReactNode;
};

/** Phone-friendly stand-in for a table row. */
const MobileEntityCard: React.FC<MobileEntityCardProps> = ({
  title,
  subtitle,
  chips,
  meta,
  actions,
}) => (
  <Paper
    sx={{
      p: 1.5,
      borderRadius: 3,
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      maxWidth: '100%',
      minWidth: 0,
      overflow: 'hidden',
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 700,
          overflowWrap: 'anywhere',
          wordBreak: 'normal',
          whiteSpace: 'normal',
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ overflowWrap: 'anywhere', mt: 0.25 }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>

    {chips && (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ maxWidth: '100%' }}>
        {chips}
      </Stack>
    )}

    {meta && meta.length > 0 && (
      <Stack spacing={0.4}>
        {meta.map((m) => (
          <Box
            key={String(m.label)}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(72px, 34%) 1fr',
              gap: 1,
              alignItems: 'start',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {m.label}
            </Typography>
            <Typography
              variant="caption"
              sx={{ textAlign: 'right', overflowWrap: 'anywhere', minWidth: 0 }}
            >
              {m.value}
            </Typography>
          </Box>
        ))}
      </Stack>
    )}

    {actions && (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ pt: 0.25 }}>
        {actions}
      </Stack>
    )}
  </Paper>
);

export const SoftChip: React.FC<{ label: string; color?: 'default' | 'success' | 'warning' | 'error' | 'info' }> = ({
  label,
  color = 'default',
}) => <Chip size="small" label={label} color={color === 'default' ? undefined : color} />;

export const ActionBtn: React.FC<React.ComponentProps<typeof Button>> = (props) => (
  <Button size="small" variant="outlined" {...props} />
);

export default MobileEntityCard;
